import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import { S } from '@tf/utils';
import { useCallback, useMemo } from 'react';

import { useApplicationMainReview } from '@/components/application/ApplicationProvider';
import { useDefinitions } from '@/core/providers';

type ConnectionLabels = { singular: string; plural: string };
type SingleConnectionLabelsFn = (k: BackendTypes.EntityConnectionKind) => ConnectionLabels;
type DynamicConnectionLabelsFn = (e: BackendTypes.EntityKind) => SingleConnectionLabelsFn;

export function useGetConnectionLabels(entityKind: BackendTypes.EntityKind): SingleConnectionLabelsFn;
export function useGetConnectionLabels(): DynamicConnectionLabelsFn;
export function useGetConnectionLabels(entityKind?: BackendTypes.EntityKind) {
	const { _ } = useLingui();
	const { defs } = useDefinitions();
	const mainReview = useApplicationMainReview();
	const applicantKind = mainReview.listEntities.find((e) => e.graphNodeId === mainReview.graphNodeId)?.entityKind;

	const getLabels = useMemo(() => {
		return (eKind: BackendTypes.EntityKind, cKind: BackendTypes.EntityConnectionKind) => {
			if (cKind === 'APPLICANT' && applicantKind === 'INDIVIDUAL_ENTITY') {
				return {
					singular: _(msg`Profile relationship structure`),
					plural: _(msg`Profile relationship structure`),
				};
			}
			if (cKind === 'APPLICANT') {
				return {
					singular: _(msg`Ownership & management structure`),
					plural: _(msg`Ownership & management structure`),
				};
			}
			const labels = defs.entities[eKind]?.possibleConnections.find((c) => c.kind === cKind)?.labels;
			if (labels) {
				return labels;
			}
			return {
				singular: S.prettify(eKind),
				plural: S.prettify(eKind),
			};
		};
	}, [_, defs.entities, applicantKind]);

	if (entityKind) {
		return useCallback(
			(connectionKind: BackendTypes.EntityConnectionKind) => getLabels(entityKind, connectionKind),
			[entityKind, getLabels],
		);
	}

	return useCallback(
		(dynamicEntityKind: BackendTypes.EntityKind) => (connectionKind: BackendTypes.EntityConnectionKind) =>
			getLabels(dynamicEntityKind, connectionKind),
		[getLabels],
	);
}

export const useGetEntityLabel = () => {
	const { defs } = useDefinitions();

	return (entityKind: BackendTypes.EntityKind) => defs.entities[entityKind]?.label ?? S.prettify(entityKind);
};
